import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  ExerciseModel,
  ProblemModel,
  ProblemOptionsModel,
  StudentStatsModel,
  SolutionModel,
  LessonModel,
  StudentSmartSavers,
  ExerciseRoundModel
} from '@generativ/much-smarter-models';
import { environment } from '../../../../../environments/environment';
import { ErrorHandlerService } from '../../../../core/services/error-handler.service';
import { ProblemInfo } from './ProblemInfo';


@Injectable({ providedIn: 'root' })
export class ExerciseService {

  private progressUrl = environment.msStudentApiUrl + 'progress/';
  private smartSaversUrl = environment.msStudentApiUrl + 'smartSavers/';
  private exerciseUrl = environment.msStudentApiUrl + 'exercises/';

  currentExercise: ExerciseModel;
  currentExerciseProblemsLength: number;
  currentLesson: LessonModel;
  problemsPending: ProblemModel[];
  lessonProgressId: number;
  solution: SolutionModel;
  smartSavers: StudentSmartSavers;

  // Persistence round.
  timePerProblemPersistenceRound: number[];
  resultPerProblemPersistenceRound: number[];


  constructor(private http: HttpClient,
              private errorHandlerService: ErrorHandlerService) {
    this.lessonProgressId = +localStorage.getItem('lessonProgressId');
    this.solution = JSON.parse(localStorage.getItem('solution'));
    this.currentLesson = JSON.parse(localStorage.getItem('lesson'));
    this.currentExercise = JSON.parse(localStorage.getItem('currentExercise'));
    this.problemsPending = JSON.parse(localStorage.getItem('problemsPending'));
    this.smartSavers = JSON.parse(localStorage.getItem('smartSavers'));
    this.timePerProblemPersistenceRound = JSON.parse(localStorage.getItem('timePerProblemPersistenceRound'));
    this.resultPerProblemPersistenceRound = JSON.parse(localStorage.getItem('resultPerProblemPersistenceRound'));
    this.smartSavers = new StudentSmartSavers();
  }


  startExercise(exerciseId: number, isPersisted = false): Observable<ExerciseModel> {
    const url = isPersisted ? `startExercisePersistenceRound/${this.lessonProgressId}/` : 'startExerciseRound/';

    if (isPersisted) {
      this.timePerProblemPersistenceRound = [];
      this.resultPerProblemPersistenceRound = [];
    }

    return new Observable(observer => {

      this.http.get<ExerciseRoundModel>(this.progressUrl + url + exerciseId).subscribe(
        exerciseRoundModel => {
          this.problemsPending = exerciseRoundModel.exercise.problems;
          this.currentExercise = exerciseRoundModel.exercise;
          this.currentExerciseProblemsLength = exerciseRoundModel.totalProblemCount;
          localStorage.setItem('lessonProgressId', this.lessonProgressId.toString());
          localStorage.setItem('currentExercise', JSON.stringify(this.currentExercise));
          localStorage.setItem('problemsPending', JSON.stringify(this.problemsPending));
          observer.next(exerciseRoundModel.exercise);
          observer.complete();

          // Get SmartSavers

          this.http.get<StudentSmartSavers>(this.smartSaversUrl).subscribe(
            studentSmartSavers => {
              localStorage.setItem('smartSavers', JSON.stringify(studentSmartSavers));
              this.smartSavers.stopTimer = studentSmartSavers.stopTimer;
              this.smartSavers.cutIncorrect = studentSmartSavers.cutIncorrect;
              this.smartSavers.getHint = studentSmartSavers.getHint;
            },
            error => {
              this.errorHandlerService.handleError(error);
            }
          );


        },
        error => {
          this.errorHandlerService.handleError(error);
        }
      );
    });
  }

  getSmartSavers(): StudentSmartSavers {
    return this.smartSavers;
  }

  getCurrentExerciseProblemCount(): number {
    return this.currentExerciseProblemsLength;
  }

  get problemsPendingLength(): number {
    return this.problemsPending.length;
  }

  saveResultPersistenceRound(time: number, result: number) {
    this.timePerProblemPersistenceRound.push(time);
    this.resultPerProblemPersistenceRound.push(result);
    localStorage.setItem('timePerProblemPersistenceRound', JSON.stringify(this.timePerProblemPersistenceRound));
    localStorage.setItem('resultPerProblemPersistenceRound', JSON.stringify(this.resultPerProblemPersistenceRound));
  }

  getCurrentProblem(): Observable<ProblemInfo> {
    const problemInfo = new ProblemInfo();
    const problem = this.problemsPending[0];
    problemInfo.problem = this.createProblemOptionsModel(problem);
    problemInfo.solutionId = problem.solutions[0].id;
    problemInfo.isLastProblem = this.problemsPending.length === 1;
    return of(problemInfo);
  }

  /**
   * After current problem is answered we should update the problem[].
   */
  updateProblemOfExercise() {
    this.problemsPending.splice(0, 1);
  }

  useSmartSaver(type: string) {
    this.http.patch<StudentSmartSavers>(this.smartSaversUrl + type, {}).subscribe(smartSaver => {
      this.smartSavers = smartSaver;
    });
  }

  setCurrentLesson(lesson: LessonModel) {
    this.currentLesson = lesson;
    localStorage.setItem('lesson', JSON.stringify(lesson));
  }


  getExercise(id: number): Observable<ExerciseModel> {
    return this.http.get<ExerciseModel>(this.exerciseUrl + id).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  getPersistenceScore(): Observable<StudentStatsModel> {
    return new Observable(observer => {

      this.http.post<StudentStatsModel>(this.smartSaversUrl + 'earnSmartSaver', {}).subscribe(
        studentStatsModel => {
          studentStatsModel.percentCorrect = this.averageFromArray(this.resultPerProblemPersistenceRound) * 100;
          studentStatsModel.secondsPerQuestion = this.averageFromArray(this.timePerProblemPersistenceRound);
          observer.next(studentStatsModel);
          observer.complete();
        },
        error => {
          this.errorHandlerService.handleError(error);
        }
      );
    });
  }

  averageFromArray(array: number[]): number {
    const sum = array.reduce((a, b) => a + b, 0);
    return sum / array.length;
  }


  getScore(exerciseId: number): Observable<StudentStatsModel> {
    return this.http.get<StudentStatsModel>(`${this.progressUrl}score/${this.lessonProgressId}/${exerciseId}`).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  getLessonScore(): Observable<StudentStatsModel> {
    return this.http.get<StudentStatsModel>(`${this.progressUrl}score/${this.lessonProgressId}`).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  getIncorrectOptions(): Observable<string[]> {
    const result = JSON.parse(this.solution.wrongAnswers).sort(() => Math.random() - 0.5);
    return of([result[0], result[1]]);
  }

  getHint(): Observable<string> {
    const result = this.problemsPending[0].topic ? this.problemsPending[0].topic.displayName : '';
    return of(result);
  }

  skipProblem() {
    const currentProblem = this.problemsPending.shift();
    this.problemsPending = [...this.problemsPending, currentProblem];
  }

  get isLastExercise(): boolean {
    const lastExercise = this.currentLesson.exercises[this.currentLesson.exercises.length - 1];
    return this.currentExercise.id === lastExercise.id;
  }

  private createProblemOptionsModel(problem: ProblemModel): ProblemOptionsModel {
    const result = new ProblemOptionsModel();
    result.answers = this.getOptionsFromProblem(problem);
    result.id = problem.id;
    result.content = problem.content;
    result.title = problem.title;
    result.solutionId = problem.solutions[0].id;
    result.passage = problem.passage;
    return result;
  }

  private getOptionsFromProblem(problem: ProblemModel): string[] {
    const solution = problem.solutions[0];
    this.solution = solution;
    localStorage.setItem('solution', JSON.stringify(solution));
    const wrongAnswers = JSON.parse(solution.wrongAnswers);
    const options = [solution.correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    return options;
  }
}
